import * as Moment from 'moment';
import 'moment-duration-format';
import { CoughSession, Subject, DeviceTypes, QueryCondition, CoughSessionStatusEnum } from "../../../misc/api/apiTypes";
import { FailedToConfigureMessage, FailedToFormatMessage, FailedToSetMessage } from "../../../misc/api/DeviceLinkApi/VitaloJak2Messages";
import { ApiFormat, ApiSetStudy, ApiSetSite, ApiSetSubject, ApiDeleteSessions, ApiAddSession, ApiSetDateTime, MemoryTarget, VitaloJak2Configuration, ApiSetConfiguration } from '../../../misc/api/DeviceLinkApi/VitaloJak2';
import { Device } from '../../../misc/api/DeviceLinkApi/Devices';
import * as VitaloJak2Api from '../../../misc/api/DeviceLinkApi/VitaloJak2';
import { StudyScheduleVisit, StudyScheduleSession, WebPortalClient, Visit } from 'webportalclient';
import { useWebPortalApiClient, WebPortalLegacyClientInternal } from '../../../misc/api/WebPortalTokenClient';

const setConfiguration = async (legacyClient: WebPortalLegacyClientInternal, currentStudyId: string, deviceId: string, buttons: Array<boolean>, onError: (message: string) => void) => {

    const coughSettings = await legacyClient.getStudyCoughSettings();

    const configuration: VitaloJak2Configuration = {
        hardwareTestEnabled: false,
        sampleRate: 8000, // Hardcoded for now
        button1Enabled: buttons[0],
        button2Enabled: buttons[1],
        button3Enabled: buttons[2],
        button4Enabled: buttons[3] || coughSettings.MuteEnabled,
        memoryTarget: MemoryTarget.Internal, // Hardcoded for now until SD workflow complete
        muteEnabled: coughSettings.MuteEnabled,
        visitSelectionEnabled: coughSettings.VisitSelection === true,
    };
    const result = await ApiSetConfiguration(deviceId, configuration);

    if (!result) {
        return onError(FailedToConfigureMessage);
    }
}

const createSessionWrappers = async (legacyClient: WebPortalLegacyClientInternal, visits: Array<Visit>, sessionScheduleItems: StudyScheduleSession[], technician: string, serialNumber: string, mediaSerialNumber: string): Promise<Array<CoughSession>> => {
    const sessions: Array<CoughSession> = [];

    for (const sessionScheduleItem of sessionScheduleItems) {
        const visitParent = visits.find(x => x.visitScheduleItemId === sessionScheduleItem.studyScheduleVisitId);

        const serialNumberTrimmed = serialNumber.replace('MD', '');

        const sessionResult = await legacyClient.createCoughSession({
            SessionScheduleId: sessionScheduleItem.id,
            VisitId: visitParent.id,
            ConfigurationTechnician: technician,
            DeviceSerialNumber: serialNumberTrimmed,
            MediaSerialNumber: mediaSerialNumber,
            StatusId: CoughSessionStatusEnum.AwaitingData,
            DeviceTypeId: DeviceTypes.VitaloJAK2,
        });

        sessions.push(sessionResult);
    }

    return sessions;
}

const createVisitWrappersIfNotExists = async (webPortalClient: WebPortalClient, visitScheduleItemIds: Array<string>, subjectId: string): Promise<Array<Visit>> => {
    const visits: Array<Visit> = [];

    for (const visitScheduleItemId of visitScheduleItemIds) {
        const visitsQuery = await webPortalClient.getVisits(undefined, `subjectId eq ${subjectId} and visitScheduleItemId eq ${visitScheduleItemId}`, undefined, undefined, undefined, undefined, undefined, true);


        if (visitsQuery['@odata.count'] === 0) {
            const scheduleVisit = await webPortalClient.getStudyScheduleVisit(visitScheduleItemId, "id,label");

            const visitResult = await webPortalClient.postVisit({
                subjectId: subjectId,
                name: scheduleVisit.label,
                occurred: Moment().toISOString()
            });

            visits.push(visitResult);
        }
        else {
            visits.push(visitsQuery.value[0]);
        }
    }

    return visits;
}

export const ConfigureVitaloJak2 = async (legacyClient: WebPortalLegacyClientInternal, client: WebPortalClient, userEmail: string, subject: Subject, outstandingVisits: StudyScheduleVisit[], sessionScheduleItems: StudyScheduleSession[], selectedDevice: Device, onError: (message: string) => void, onComplete: () => void) => {

    try {
        const recordingsOnDevice = await VitaloJak2Api.ApiGetRecordings(selectedDevice.id);
        if (recordingsOnDevice.length !== 0) {
            const uploadRecordingCompletedFilters = [{
                Member: 'Id',
                Values: recordingsOnDevice.map(x => x.externalCode),
            }, {
                Member: 'UploadEndTime',
                Values: [null],
                Condition: QueryCondition.NotEqual,
            }];

            const completedCoughSessions = await legacyClient.getCoughSessions(uploadRecordingCompletedFilters);

            if (completedCoughSessions.Data.length === recordingsOnDevice.length) {

                await VitaloJak2Api.ApiReset(selectedDevice.id);

            }
        }
    }
    catch (err) {
        onError(FailedToConfigureMessage);
    }
    try {
        const currentStudyId = subject.StudyId;

        const deviceId = selectedDevice.id;

        const coughSettings = await legacyClient.getStudyCoughSettings();

        const sessionWrappersSorted: Array<CoughSession> = [];
        const sessionIds = sessionScheduleItems.map(x => x.id);
        const serialOfDeviceWithoutPrefix = selectedDevice.serial.replace("MD", "");

        const sessionWrappers = await legacyClient.getCoughSessions([
            { Member: 'Id', Values: sessionIds }
        ]).then(x => x.Data);

        const sessionScheduleItemsWithoutSession = sessionScheduleItems.filter(x => !sessionWrappers.map(x => x.SessionScheduleId).includes(x.id));

        let allSessions: Array<CoughSession> = [];
        if (sessionScheduleItemsWithoutSession.length > 0) {
            const visitWrappers = await createVisitWrappersIfNotExists(client, sessionScheduleItems.map(s => s.studyScheduleVisitId), subject.Id);
            allSessions = sessionWrappers.concat(await createSessionWrappers(legacyClient, visitWrappers, sessionScheduleItemsWithoutSession, userEmail, selectedDevice.serial, null));
        } else {
            allSessions = sessionWrappers;
        }

        outstandingVisits.forEach((visit) => {
            visit.studyScheduleSessions.forEach((session) => {
                const item = allSessions.find(x => x.SessionScheduleId === session.id);
                if (item)
                    sessionWrappersSorted.push(item);
            });
        });

        const coughSessions = await client.getCoughSessions(undefined, `not(id in (${sessionWrappersSorted.map(s => s.Id).join(",")})) and status eq '${CoughSessionStatusEnum.AwaitingData}' and uploadEndTime eq null and availableForQualityCheckDateTime eq null and serialNumber eq '${serialOfDeviceWithoutPrefix}'`);

        for (const session of coughSessions.value) {
            await client.patchCoughSession({
                suppressed: true,
                configured: false,
            }, session.id); 
        }

        const formatResult = await ApiFormat(deviceId);
        if (!formatResult) {
            return onError(FailedToFormatMessage);
        }

        await setConfiguration(legacyClient, currentStudyId, deviceId, coughSettings.ButtonsEnabled, onError);

        const studySetResult = await ApiSetStudy(deviceId, { studyId: sessionWrappersSorted[0].Study.ProtocolNumber });
        if (!studySetResult) {
            return onError(FailedToConfigureMessage + " " + FailedToSetMessage("study"));
        }

        const siteSetResult = await ApiSetSite(deviceId, { siteId: sessionWrappersSorted[0].Site.SiteNumber });
        if (!siteSetResult) {
            return onError(FailedToConfigureMessage + " " + FailedToSetMessage('site'));
        }

        const subjectSetResult = await ApiSetSubject(deviceId, { subjectId: sessionWrappersSorted[0].Subject.SubjectNumber });
        if (!subjectSetResult) {
            return onError(FailedToConfigureMessage + " " + FailedToSetMessage('subject'));
        }

        const deleteSessionsResult = await ApiDeleteSessions(deviceId);
        if (!deleteSessionsResult) {
            return onError(FailedToConfigureMessage + " " + FailedToSetMessage('session(s)'));
        }

        for (const session of sessionWrappersSorted) {
            const result = await ApiAddSession(deviceId, { externalCode: session.Id, recordingLength: coughSettings.RecordingDuration, sessionId: session.Name.substring(0, 9) });

            if (!result) {
                return onError(FailedToConfigureMessage);
            }
        }

        for (const session of sessionWrappersSorted) {
            await client.patchCoughSession({
                suppressed: false,
            }, session.Id);
        }

        for (const session of sessionWrappersSorted) {
            await client.patchCoughSession({
                configured: true,
            }, session.Id);
        }

        const now = Moment().local();
        const dateTimeSetResult = await ApiSetDateTime(deviceId, { year: parseInt(now.format('YY')), month: now.month() + 1 /* months are zero based */, day: now.date(), hours: now.hour(), minutes: now.minutes(), seconds: now.seconds() })
        if (!dateTimeSetResult) {
            return onError(FailedToConfigureMessage + " " + FailedToSetMessage('date and time'));
        }

        onComplete();

        return;
    }
    catch (err) {
        onError(FailedToConfigureMessage);
    }
}
